import {
  HabitSchedule,
  ScheduleEntryState,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';

import { isBetween } from '../lib/date-fns';
import { getScheduleTimeframeByDate } from './get-schedule-timeframe-by-date';

export type TimeframeOccurrencesOptions = {
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const getPlannedTimeframeOccurrences = (
  schedule: HabitSchedule,
  { weekStartsOn, today }: TimeframeOccurrencesOptions,
) => {
  if (!schedule.plannedOccurrences?.length) {
    return [];
  }
  const timeframeRange = getScheduleTimeframeByDate(
    schedule,
    today,
    weekStartsOn,
  );

  return schedule.plannedOccurrences.filter(({ date }) =>
    isBetween(date, timeframeRange.startDate, timeframeRange.endDate),
  );
};

export type Options = {
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const getPlannedTimeframeDueDateBySchedule = (
  schedule: HabitSchedule,
  { weekStartsOn, today }: Options,
) => {
  // check if there are plannedOccurrences in the current timeframe
  const plannedOccurrences = getPlannedTimeframeOccurrences(schedule, {
    weekStartsOn,
    today,
  });

  if (!plannedOccurrences.length) {
    return;
  }

  const validEntries = schedule.entries.filter(
    ({ state, value }) =>
      (state === ScheduleEntryState.Complete &&
        (schedule.frequency
          ? !!value
          : (value ?? 0) >= schedule.completionTarget.count)) ||
      state !== ScheduleEntryState.Complete,
  );

  const lastEntryDate = validEntries.length
    ? new Date(Math.max(...validEntries.map(({ date }) => date.getTime())))
    : undefined;

  // filter out the plannedOccurrences that are from before the last entry
  const availablePlannedOccurrences = lastEntryDate
    ? plannedOccurrences.filter(({ date }) =>
        isAfter(startOfDay(date), lastEntryDate),
      )
    : plannedOccurrences;

  // get closest occurrence before today
  const startOfDate = startOfDay(today);
  const closestPassedOccurrence = availablePlannedOccurrences
    // sort with the latest date first
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    // get the first date that is today or is before today
    .find(
      ({ date }) => isSameDay(date, startOfDate) || isBefore(date, startOfDate),
    );

  if (closestPassedOccurrence) {
    return closestPassedOccurrence.date;
  }

  // if there is no past occurrence, take the first date that is after today
  return (
    availablePlannedOccurrences
      // sort with the earliest date first
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      // get the first date that is after today
      .find(({ date }) => isAfter(date, startOfDate))?.date
  );
};
